// File: src/pages/Reports/AllProductsMovementReport.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../../../axios';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  Autocomplete,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SearchIcon from '@mui/icons-material/Search';

export default function AllProductsMovementReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedStock, setSelectedStock] = useState('');

  const [allMovements, setAllMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD')
  );
  const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));

  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    setProductsLoading(true);
    try {
      const res = await axios.get(`/Product/by-company/${companyId}`);
      setProducts(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة الأصناف:', error);
    } finally {
      setProductsLoading(false);
    }
  }, [companyId]);

  const fetchBranches = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`/Branch/by-company/${companyId}`);
      setBranches(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة الفروع:', error);
    }
  }, [companyId]);

  const fetchStocks = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`/Stock/by-company/${companyId}`);
      setStocks(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة المستودعات:', error);
    }
  }, [companyId]);

  const fetchAllMovements = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        ...(selectedProduct && { productId: selectedProduct.id }),
        ...(selectedBranch && { branchId: selectedBranch }),
        ...(selectedStock && { stockId: selectedStock }),
        dateFrom: `${dateFrom}T00:00:00`,
        dateTo: `${dateTo}T23:59:59`,
      };
      const res = await axios.get(`/StockMovements/report/${companyId}`, { params });
      setAllMovements(res.data || []);
    } catch (error) {
      console.error('❌ فشل جلب جميع حركات الأصناف:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedProduct, selectedBranch, selectedStock, dateFrom, dateTo]);

  useEffect(() => {
    fetchProducts();
    fetchBranches();
    fetchStocks();
    fetchAllMovements();
  }, [fetchProducts, fetchBranches, fetchStocks, fetchAllMovements]);

  const rowsToDisplay = useMemo(
    () =>
      allMovements.map((item) => ({
        id: item.id,
        productName: item.productName,
        stockName: item.stockName,
        branchId: item.branchId,
        movementDate: dayjs(item.movementDate).format('YYYY-MM-DD HH:mm'),
        invoiceNumber: item.invoiceNumber || '—',
        invoiceTypeName: item.invoiceTypeName || '—',
        quantityIn: item.quantityIn ?? 0,
        quantityOut: item.quantityOut ?? 0,
        unitPrice: item.unitPrice ?? 0,
        totalCost: item.totalCost ?? 0,
        runningBalance: item.runningBalance ?? 0,
      })),
    [allMovements]
  );

  // حساب الإحصائيات
  const statistics = useMemo(() => ({
    totalMovements: rowsToDisplay.length,
    totalIn: rowsToDisplay.reduce((sum, row) => sum + row.quantityIn, 0),
    totalOut: rowsToDisplay.reduce((sum, row) => sum + row.quantityOut, 0),
    totalValue: rowsToDisplay.reduce((sum, row) => sum + row.totalCost, 0),
    avgBalance: rowsToDisplay.length > 0 
      ? rowsToDisplay.reduce((sum, row) => sum + row.runningBalance, 0) / rowsToDisplay.length 
      : 0,
  }), [rowsToDisplay]);

  const columns = [
    { 
      field: 'productName', 
      headerName: 'الصنف', 
      flex: isMobile ? 0 : 1.5,
      minWidth: isMobile ? 120 : 200,
      align: 'center',
      headerAlign: 'center'
    },
    !isMobile && { 
      field: 'stockName', 
      headerName: 'المستودع', 
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center'
    },
    !isMobile && { 
      field: 'branchId', 
      headerName: 'الفرع', 
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    { 
      field: 'movementDate', 
      headerName: 'التاريخ', 
      width: isMobile ? 110 : 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="caption" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
          {params.value}
        </Typography>
      )
    },
    !isMobile && { 
      field: 'invoiceNumber', 
      headerName: 'رقم الفاتورة', 
      width: 110,
      align: 'center',
      headerAlign: 'center'
    },
    !isTablet && { 
      field: 'invoiceTypeName', 
      headerName: 'نوع الفاتورة', 
      width: 120,
      align: 'center',
      headerAlign: 'center'
    },
    { 
      field: 'quantityIn', 
      headerName: 'وارد', 
      width: isMobile ? 70 : 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value > 0 ? (
        <Chip 
          label={params.value} 
          size="small" 
          color="success" 
          variant="outlined"
          icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
        />
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )
    },
    { 
      field: 'quantityOut', 
      headerName: 'صادر', 
      width: isMobile ? 70 : 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value > 0 ? (
        <Chip 
          label={params.value} 
          size="small" 
          color="error" 
          variant="outlined"
          icon={<TrendingDownIcon sx={{ fontSize: 14 }} />}
        />
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )
    },
    {
      field: 'runningBalance',
      headerName: 'الرصيد',
      width: isMobile ? 80 : (isTablet ? 100 : 120),
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params.row.runningBalance || 0;
        const color = value < 0 ? 'error' : value === 0 ? 'default' : 'success';
        return (
          <Chip 
            label={value.toFixed(2)} 
            size="small" 
            color={color}
            sx={{ fontWeight: 'bold' }}
          />
        );
      },
    },
    !isMobile && {
      field: 'unitPrice',
      headerName: 'السعر',
      width: isTablet ? 90 : 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {(params.row.unitPrice || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'totalCost',
      headerName: 'الإجمالي',
      width: isMobile ? 90 : (isTablet ? 100 : 120),
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {(params.row.totalCost || 0).toFixed(2)}
        </Typography>
      ),
    },
   
  ].filter(Boolean);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }} dir="rtl">
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardHeader
          avatar={<TimelineIcon sx={{ fontSize: 32, color: 'white' }} />}
          title={
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
              تقرير حركة جميع الأصناف
            </Typography>
          }
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        />
        
        <CardContent>
          {/* الفلاتر */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              {/* البحث عن الصنف */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Autocomplete
                  value={selectedProduct}
                  onChange={(event, newValue) => {
                    setSelectedProduct(newValue);
                  }}
                  options={products}
                  getOptionLabel={(option) => option.arabicName || ''}
                  loading={productsLoading}
                  loadingText="جاري التحميل..."
                  noOptionsText="لا توجد نتائج"
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ابحث عن صنف"
                      placeholder="اكتب للبحث..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{option.arabicName}</Typography>
                        {option.code && (
                          <Typography variant="caption" color="text.secondary">
                            كود: {option.code}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  label="اختر فرعًا"
                  size="small"
                  fullWidth
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value=""><em>كافة الفروع</em></MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id.toString()}>{b.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  label="اختر مستودعًا"
                  size="small"
                  fullWidth
                  value={selectedStock}
                  onChange={(e) => setSelectedStock(e.target.value)}
                >
                  <MenuItem value=""><em>كافة المستودعات</em></MenuItem>
                  {stocks.map((s) => (
                    <MenuItem key={s.id} value={s.id.toString()}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  label="من تاريخ"
                  type="date"
                  size="small"
                  fullWidth
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  label="إلى تاريخ"
                  type="date"
                  size="small"
                  fullWidth
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={0.8}>
                <Button 
                  variant="contained" 
                  fullWidth
                  startIcon={<RefreshIcon />}
                  onClick={fetchAllMovements}
                  sx={{ height: 40 }}
                >
                  {isMobile ? 'عرض' : 'عرض التقرير'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* الإحصائيات */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'primary.light' }}>
                <Typography variant="caption" color="white">عدد الحركات</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalMovements}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'success.light' }}>
                <Typography variant="caption" color="white">إجمالي الوارد</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalIn.toFixed(0)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'error.light' }}>
                <Typography variant="caption" color="white">إجمالي الصادر</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalOut.toFixed(0)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'warning.light' }}>
                <Typography variant="caption" color="white">إجمالي القيمة</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalValue.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'info.light' }}>
                <Typography variant="caption" color="white">متوسط الرصيد</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.avgBalance.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* الجدول */}
          <Box sx={{ 
            height: isMobile ? 500 : 600, 
            width: '100%',
            '& .MuiDataGrid-root': {
              border: 'none',
              borderRadius: 2,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              borderRadius: '8px 8px 0 0',
              fontWeight: 'bold',
            },
          }}>
            <DataGrid
              rows={rowsToDisplay}
              columns={columns}
              loading={loading}
              components={{ Toolbar: GridToolbar }}
              pageSize={isMobile ? 10 : 25}
              rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
              disableSelectionOnClick
              density={isMobile ? 'compact' : 'standard'}
              sx={{
                '& .MuiDataGrid-cell': { 
                  textAlign: 'center',
                  fontSize: isMobile ? '0.7rem' : '0.875rem'
                },
                '& .MuiDataGrid-columnHeader': { 
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.7rem' : '0.875rem'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}